import Button from 'components/common/Button';
import { useAddressPageContext } from 'lib/hooks/page-context/AddressPageContext';
import { parseInputAddress } from 'lib/utils/whois';
import { useTranslations } from 'next-intl';
import { useEffect, useRef, useState } from 'react';
import { twMerge } from 'tailwind-merge';
import type { Address } from 'viem';
import { useAccount } from 'wagmi';
import AddressSearchBox from '../common/AddressSearchBox';
import ChainSelect from '../common/select/ChainSelect';

interface Props {
  address?: Address;
  chainIds?: number[];
  onSubmit: (address: Address) => void;
  placeholder: string;
}

export const AddressForm = ({ address: passedAddress, chainIds, onSubmit, placeholder }: Props) => {
  const t = useTranslations();

  const [value, setValue] = useState<string>(passedAddress || '');
  const [isFocused, setIsFocused] = useState<boolean>(false);
  const timerRef = useRef<NodeJS.Timeout | undefined>(undefined);

  const { address } = useAccount();
  const { selectedChainId, selectChain } = useAddressPageContext();

  useEffect(() => {
    if (passedAddress) setValue(passedAddress);
  }, [passedAddress]);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    const inputElement = (event.target as HTMLFormElement)[0] as HTMLInputElement;
    const address = await parseInputAddress(inputElement.value);

    if (address) onSubmit(address);
  };

  const onFocus = () => {
    clearTimeout(timerRef.current);
    setIsFocused(true);
  };

  const onBlur = () => {
    timerRef.current = setTimeout(() => setIsFocused(false), 200);
  };

  const onClick = () => {
    if (address) {
      setValue(address);
      handleSubmit({ target: [{ value: address }] } as any);
    }
  };

  return (
    <div className="relative flex flex-col gap-2">
      <div className="flex gap-2">
        <AddressSearchBox
          id="exploit-search"
          onSubmit={handleSubmit}
          onChange={(ev) => setValue(ev.target.value.trim())}
          value={value}
          placeholder={placeholder}
          className="w-full text-base sm:text-lg"
          onFocus={onFocus}
          onBlur={onBlur}
        />
        {chainIds && (
          <ChainSelect
            instanceId="exploit-chain-select"
            chainIds={chainIds}
            selected={selectedChainId}
            onSelect={selectChain}
          />
        )}
        <div className={twMerge('absolute -top-10', (!isFocused || !address) && 'hidden')}>
          <Button style="secondary" size="md" onClick={onClick}>
            {t('common.buttons.check_connected_address')}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AddressForm;
